import { aggregateReportSchema, scanReportSchema } from '@submitgate/contracts';

function $(sel: string): HTMLElement {
  const el = document.querySelector(sel);
  if (!el) throw new Error(`Missing element: ${sel}`);
  return el as HTMLElement;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Record<string, any> = {},
  children: Array<Node | string | null | undefined> = [],
): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs || {})) {
    if (k === 'class') (e as any).className = v;
    else if (k.startsWith('on') && typeof v === 'function')
      e.addEventListener(k.slice(2), v);
    else if (v !== undefined && v !== null) e.setAttribute(k, String(v));
  }
  for (const c of children) {
    if (c === null || c === undefined) continue;
    e.appendChild(typeof c === 'string' ? document.createTextNode(c) : c);
  }
  return e;
}

type Severity = 'BLOCKER' | 'WARNING' | 'INFO';

function severityOrder(s: string): number {
  if (s === 'BLOCKER') return 0;
  if (s === 'WARNING') return 1;
  return 2;
}

function computeCounts(list: any[]): {
  BLOCKER: number;
  WARNING: number;
  INFO: number;
  total: number;
} {
  const c = { BLOCKER: 0, WARNING: 0, INFO: 0, total: 0 };
  for (const r of list || []) {
    if (!r || !r.severity) continue;
    if ((c as any)[r.severity] !== undefined) (c as any)[r.severity]++;
    c.total++;
  }
  return c;
}

function isScanReportHeuristic(r: any): boolean {
  return (
    r &&
    typeof r === 'object' &&
    (r.platform === 'ios' || r.platform === 'android') &&
    Array.isArray(r.results)
  );
}

function isAggregateReportHeuristic(r: any): boolean {
  return (
    r &&
    typeof r === 'object' &&
    r.schemaVersion === 1 &&
    Array.isArray(r.items) &&
    r.totals &&
    r.aggregateVersion
  );
}

const drop = $('#drop');
const file = $('#file') as HTMLInputElement;

const controls = $('#controls');
const summary = $('#summary');
const results = $('#results');
const errorBox = $('#error');
const errorText = $('#errorText');

const fBlocker = $('#fBlocker') as HTMLInputElement;
const fWarning = $('#fWarning') as HTMLInputElement;
const fInfo = $('#fInfo') as HTMLInputElement;
const search = $('#search') as HTMLInputElement;
const clear = $('#clear') as HTMLButtonElement;

const itemSelectRow = $('#itemSelectRow');
const itemSelect = $('#itemSelect') as unknown as HTMLSelectElement;

let model: any = null; // { kind, report, selectedItemKey? }

function showError(msg: string) {
  errorBox.style.display = 'block';
  errorText.textContent = msg;
  controls.style.display = 'none';
  summary.style.display = 'none';
  results.style.display = 'none';
}

function hideError() {
  errorBox.style.display = 'none';
  errorText.textContent = '';
}

function renderSummaryForScan(r: any) {
  const counts = computeCounts(r.results);
  summary.innerHTML = '';
  summary.appendChild(
    el(
      'div',
      {
        class: 'row',
        style: 'justify-content:space-between; align-items:baseline',
      },
      [
        el('div', {}, [
          el('div', { class: 'h2' }, ['Scan Report']),
          el('div', { class: 'row', style: 'gap:6px; margin-top:4px' }, [
            el('span', { class: 'meta-tag' }, [
              el('span', { class: 'meta-label' }, ['Platform']),
              String(r.platform ?? '?'),
            ]),
            el('span', { class: 'meta-tag' }, [
              el('span', { class: 'meta-label' }, ['Kind']),
              String(r.artifactKind ?? '?'),
            ]),
            el('span', { class: 'meta-tag' }, [
              el('span', { class: 'meta-label' }, ['Version']),
              String(r.scanVersion ?? '?'),
            ]),
          ]),
        ]),
        el('div', { class: 'row' }, [
          el('span', { class: 'tag blocker' }, [`BLOCKER ${counts.BLOCKER}`]),
          el('span', { class: 'tag warning' }, [`WARNING ${counts.WARNING}`]),
          el('span', { class: 'tag info' }, [`INFO ${counts.INFO}`]),
          el('span', { class: 'tag' }, [`Total ${counts.total}`]),
        ]),
      ],
    ),
  );

  if (r.coverage) {
    const notes = r.coverage.notes || [];
    const coverageRow = el(
      'div',
      { class: 'row', style: 'margin-top:12px; gap:8px; align-items:center' },
      [
        el('span', { class: `tag ${r.coverage.full ? 'info' : 'warning'}` }, [
          r.coverage.full ? 'Full Coverage' : 'Partial Coverage',
        ]),
        notes.length > 0
          ? el('span', { class: 'small' }, [
              `${notes.length} coverage note${notes.length === 1 ? '' : 's'}`,
            ])
          : null,
      ],
    );
    summary.appendChild(coverageRow);

    if (notes.length > 0) {
      const notesList = el('div', { class: 'coverage-notes' });
      for (const note of notes) {
        notesList.appendChild(
          el('div', { class: 'coverage-note' }, [
            el('span', { class: 'coverage-note-rule' }, [
              String(note.ruleId || ''),
            ]),
            el('span', {}, [String(note.message || '')]),
          ]),
        );
      }
      summary.appendChild(notesList);
    }
  }
}

function renderSummaryForAggregate(ar: any) {
  const c = ar.totals?.findings || {
    BLOCKER: 0,
    WARNING: 0,
    INFO: 0,
    total: 0,
  };
  summary.innerHTML = '';
  summary.appendChild(
    el(
      'div',
      {
        class: 'row',
        style: 'justify-content:space-between; align-items:baseline',
      },
      [
        el('div', {}, [
          el('div', { class: 'h2' }, ['Aggregate Report']),
          el('div', { class: 'small' }, [
            `aggregateVersion=${ar.aggregateVersion ?? '?'} · items=${ar.items?.length ?? 0}`,
          ]),
        ]),
        el('div', { class: 'row' }, [
          el('span', { class: 'tag blocker' }, [`BLOCKER ${c.BLOCKER}`]),
          el('span', { class: 'tag warning' }, [`WARNING ${c.WARNING}`]),
          el('span', { class: 'tag info' }, [`INFO ${c.INFO}`]),
          el('span', { class: 'tag' }, [`Total ${c.total}`]),
        ]),
      ],
    ),
  );
}

function shouldIncludeSeverity(sev: Severity) {
  if (sev === 'BLOCKER') return fBlocker.checked;
  if (sev === 'WARNING') return fWarning.checked;
  return fInfo.checked;
}

function matchesSearch(rule: any) {
  const q = search.value.trim().toLowerCase();
  if (!q) return true;
  const hay = `${rule.id || ''} ${rule.title || ''}`.toLowerCase();
  return hay.includes(q);
}

function formatEvidence(evidence: string): HTMLElement {
  const text = String(evidence || '');

  // If evidence contains semicolons with paths in parens, split into a list
  const pathPattern = /;\s*/;
  const parts = text
    .split(pathPattern)
    .map((s) => s.trim())
    .filter(Boolean);

  if (parts.length > 1) {
    const list = el('ul', { class: 'evidence-list' });
    for (const part of parts) {
      const li = el('li', {});
      // Highlight file paths inside parentheses
      const pathMatch = part.match(/^(.+?)\s*\(([^)]+)\)$/);
      if (pathMatch) {
        li.appendChild(document.createTextNode(pathMatch[1] + ' '));
        li.appendChild(
          el('span', { class: 'evidence-path' }, [`(${pathMatch[2]})`]),
        );
      } else {
        li.textContent = part;
      }
      list.appendChild(li);
    }
    return list;
  }

  // Single block of evidence text
  const block = el('div', { class: 'evidence-text' });
  block.textContent = text;
  return block;
}

function formatRemediation(text: string): HTMLElement {
  const container = el('div', { class: 'detail-remediation' });
  // Auto-link URLs
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = String(text || '').split(urlRegex);
  for (const part of parts) {
    if (urlRegex.test(part)) {
      urlRegex.lastIndex = 0;
      container.appendChild(
        el('a', { href: part, target: '_blank', rel: 'noopener' }, [part]),
      );
    } else {
      container.appendChild(document.createTextNode(part));
    }
  }
  return container;
}

function renderResults(list: any[]) {
  const filtered = (list || [])
    .filter((r) => r && shouldIncludeSeverity(r.severity) && matchesSearch(r))
    .slice()
    .sort((a, b) => {
      const d = severityOrder(a.severity) - severityOrder(b.severity);
      if (d !== 0) return d;
      return String(a.id).localeCompare(String(b.id));
    });

  results.innerHTML = '';
  results.appendChild(
    el('div', { class: 'h2' }, [`Findings (${filtered.length})`]),
  );

  const container = el('div', { class: 'findings-list' });
  for (const r of filtered) {
    const sevClass =
      r.severity === 'BLOCKER'
        ? 'blocker'
        : r.severity === 'WARNING'
          ? 'warning'
          : 'info';

    const sevCardClass =
      r.severity === 'BLOCKER'
        ? 'sev-blocker'
        : r.severity === 'WARNING'
          ? 'sev-warning'
          : 'sev-info';

    const details = el('details', { class: 'finding-details' }, [
      el('summary', {}, ['Details']),
      el('div', { class: 'detail-section' }, [
        el('div', { class: 'detail-label' }, ['Description']),
        el('div', { class: 'detail-description' }, [
          String(r.description || ''),
        ]),
      ]),
      el('div', { class: 'detail-section' }, [
        el('div', { class: 'detail-label' }, ['Evidence']),
        formatEvidence(r.evidence),
      ]),
      el('div', { class: 'detail-section' }, [
        el('div', { class: 'detail-label' }, ['Remediation']),
        formatRemediation(r.remediation),
      ]),
      el('div', { class: 'finding-fingerprint' }, [
        `Fingerprint: ${r.fingerprint || ''}`,
      ]),
    ]);

    const card = el('div', { class: `finding-card ${sevCardClass}` }, [
      el('div', { class: 'finding-header' }, [
        el('span', { class: `tag ${sevClass}` }, [r.severity]),
        el('span', { class: 'finding-rule' }, [String(r.id || '')]),
      ]),
      el('div', { class: 'finding-title' }, [String(r.title || '')]),
      details,
    ]);
    container.appendChild(card);
  }
  results.appendChild(container);
}

function refresh() {
  if (!model) return;
  hideError();
  controls.style.display = 'block';
  summary.style.display = 'block';
  results.style.display = 'block';

  if (model.kind === 'scan') {
    itemSelectRow.style.display = 'none';
    renderSummaryForScan(model.report);
    renderResults(model.report.results || []);
  } else {
    itemSelectRow.style.display = 'flex';
    renderSummaryForAggregate(model.report);
    const item =
      model.report.items.find((i: any) => i.key === model.selectedItemKey) ||
      model.report.items[0];
    if (item) {
      model.selectedItemKey = item.key;
      renderResults(item.results || []);
    } else {
      renderResults([]);
    }
  }
}

function setAggregateSelect(ar: any) {
  itemSelect.innerHTML = '';
  for (const it of ar.items || []) {
    const total = it.counts?.total ?? it.results?.length ?? 0;
    const label =
      `${it.platform}/${it.artifactKind}` +
      (it.appId ? ` · ${it.appId}` : '') +
      ` · findings ${total}`;
    itemSelect.appendChild(el('option', { value: it.key }, [label]));
  }

  itemSelect.addEventListener('change', () => {
    model.selectedItemKey = itemSelect.value;
    refresh();
  });
}

function parseReport(
  parsed: any,
): { kind: 'scan'; report: any } | { kind: 'aggregate'; report: any } {
  // Preferred: validate against the shared contract schema.
  const scan = scanReportSchema.safeParse(parsed);
  if (scan.success) return { kind: 'scan', report: scan.data };

  const agg = aggregateReportSchema.safeParse(parsed);
  if (agg.success) return { kind: 'aggregate', report: agg.data };

  // Fallback: be tolerant to older reports while still rendering.
  if (isScanReportHeuristic(parsed)) return { kind: 'scan', report: parsed };
  if (isAggregateReportHeuristic(parsed))
    return { kind: 'aggregate', report: parsed };

  // Show useful error (keep it short)
  const issues = [...scan.error.issues, ...agg.error.issues];
  const msg = issues
    .slice(0, 8)
    .map((i) => `${i.path.join('.') || '<root>'}: ${i.message}`)
    .join('\n');
  throw new Error(msg || 'Unrecognized report format.');
}

async function loadText(text: string) {
  let parsed: any;
  try {
    parsed = JSON.parse(text);
  } catch {
    showError('Invalid JSON');
    return;
  }

  try {
    const res = parseReport(parsed);
    if (res.kind === 'scan') {
      model = { kind: 'scan', report: res.report };
    } else {
      model = {
        kind: 'aggregate',
        report: res.report,
        selectedItemKey: res.report.items?.[0]?.key,
      };
      setAggregateSelect(res.report);
    }
    refresh();
  } catch (e: any) {
    showError(String(e?.message || e || 'Unrecognized report format.'));
  }
}

function handleFile(f: File) {
  const reader = new FileReader();
  reader.onload = () => loadText(String(reader.result || ''));
  reader.onerror = () => showError('Failed to read file');
  reader.readAsText(f);
}

file.addEventListener('change', () => {
  const f = file.files?.[0];
  if (f) handleFile(f);
});

drop.addEventListener('dragover', (e) => {
  e.preventDefault();
  drop.classList.add('dragover');
});

drop.addEventListener('dragleave', () => {
  drop.classList.remove('dragover');
});

drop.addEventListener('drop', (e: DragEvent) => {
  e.preventDefault();
  drop.classList.remove('dragover');
  const f = e.dataTransfer?.files?.[0];
  if (f) handleFile(f);
});

for (const ctl of [fBlocker, fWarning, fInfo, search]) {
  ctl.addEventListener('input', refresh);
  ctl.addEventListener('change', refresh);
}

clear.addEventListener('click', () => {
  search.value = '';
  refresh();
});
